import React, { Component } from "react"
import PropTypes from "prop-types"
import { Summary, Hyperlink } from "components"
import { H5, FormGroup, Space, AlertIcon } from "AtomicComponents"
import { formatAddress, pick } from "utils"
import { resendMyMazdaVerificationEmail } from "apis/mymazda"
import { connect } from "react-redux"
import { actionCreators } from "store/actions/mymazda"
import { withRadium, Radium, keyframes } from "higher-order-components"
import COLOR_PALETTE from "constants/color-palette"
import { MYMAZDA_ERRORS } from "constants/errors"
import EditPassword from "./edit-password"
import EditEmailAddress from "./edit-email-address"
import { MyMazdaPersonalDetails } from "AtomicContainers"
import gtmHandler from "AtomicUtils/gtmHandler";


const createDetail = (label, value, options = {}) => ({ label, value, ...options })

const createStateAutofill = ({ state }) => state && { name: state, value: state.toLowerCase() }
const createTitleAutofill = title => title && { name: title, value: title.toLowerCase() }

const fadeInKeyframes = keyframes({
  from: {
    opacity: 0
  }
})

const BlackoutOverlay = Radium(({ children }) => (
  <div
    style={{
      position: "relative",
      zIndex: 1
    }}>
    <div
      style={{
        position: "fixed",
        backgroundColor: "white",
        opacity: 0.9,
        left: 0,
        right: 0,
        top: 0,
        bottom: 0,
        zIndex: -1,
        animation: "x .666s",
        animationName: fadeInKeyframes
      }}
    />
    <div
      style={{
        backgroundColor: "white"
      }}>
      {children}
    </div>
  </div>
))

const pickPersonalDetails = pick([
  "title",
  "firstName",
  "surname",
  "mobilePhone",
  "afterHoursPhone",
  "businessHoursPhone",
  "businessHoursPhoneExtension",
  "address1",
  "address2",
  "address3",
  "townName",
  "postcode",
  "state",
  "driversLicenceNumber",
  "driversLicenceState"
])

const editableSections = [
  "editPersonalDetails",
  "editEmailAddress",
  "editPassword"
]


class MyMazdaAccountDetails extends Component {
  constructor(props) {
    super(props)
    this.state = {
      activeSection: -1,
      verificationEmailResent: false
    }
  }

  handleEmailAddressUpdate = ({ success }) => {
    if (success) {
      this.props.flashMessage(MYMAZDA_ERRORS.ACCOUNT_CHANGE_EMAIL_REQUESTED, "SUCCESS")
      this.props.refreshCurrentRoute()
    }
    this.setState({ activeSection: -1 })
  }

  handlePersonalDetailsUpdate = ({ success }) => {
    if (success) {
      this.props.flashMessage(MYMAZDA_ERRORS.ACCOUNT_PERSONAL_DETAILS_UPDATED, "SUCCESS")
      this.props.refreshCurrentRoute()
    }
    this.setState({ activeSection: -1 })
  }

  handlePasswordUpdate = ({ success }) => {
    if (success) {
      this.props.flashMessage(MYMAZDA_ERRORS.ACCOUNT_PASSWORD_UPDATED, "SUCCESS")
      this.props.refreshCurrentRoute()
    }
    this.setState({ activeSection: -1 })
  }

  handleResendEmail = () => {
    if (this.state.verificationEmailResent) return
    resendMyMazdaVerificationEmail(this.props.resendEmailEndpoint)
      .then(() => {
        this.props.flashMessage(MYMAZDA_ERRORS.ACCOUNT_RESEND_CHANGE_EMAIL, "SUCCESS")
        this.setState({ verificationEmailResent: true })
      })
      .catch(error => {
        this.props.flashMessage(MYMAZDA_ERRORS.ACCOUNT_RESEND_CHANGE_EMAIL_FAILED, "ERROR")
      })
  }

  render() {
    const {
      heading,
      customer,
      changePasswordEndpoint,
      accountSettingsEndpoint,
      accountEmailAddressEndpoint,
      changeEmailDescription,
      changeEmailEndpoint,
      emailPendingApproval
    } = this.props

    const {
      title,
      firstName,
      surname,
      mobilePhone,
      afterHoursPhone,
      businessHoursPhone,
      address,
      email
    } = customer
    const { activeSection, verificationEmailResent } = this.state
    const isActive = section => editableSections.indexOf(section) === activeSection

    return (
      <div>
        <div>
          <FormGroup center>
            <H5 withDetails>Personal Details</H5>
          </FormGroup>
          {isActive("editPersonalDetails") ? (
            <BlackoutOverlay>
              <MyMazdaPersonalDetails
                onUpdate={this.handlePersonalDetailsUpdate}
                updatePersonalDetailsEndpoint={accountSettingsEndpoint}
                customer={customer}
                prefillFields={pickPersonalDetails({
                  ...customer,
                  ...customer.address,
                  title: createTitleAutofill(customer.title),
                  state: createStateAutofill(customer.address)
                })} />
            </BlackoutOverlay>
          ) : (
            <Summary
              disableBase
              items={[
                {
                  ...createDetail("Name", title + " " + firstName + " " + surname),
                  editText: "Edit",
                  onClick: () => {
                    this.setState({
                      activeSection: editableSections.indexOf("editPersonalDetails")
                    }, () => {
                      gtmHandler({
                        pageCategory: "MyMazda - Manage Account",
                        pageAction: `PERSONAL DETAILS - "NAME" - clicked "EDIT"`
                      })
                    })
                  }
                },
                createDetail("Mobile Phone", mobilePhone), // May need to run formatting func here
                afterHoursPhone && createDetail("After hours Phone", afterHoursPhone),
                businessHoursPhone && createDetail("Business hours Phone", businessHoursPhone),
                createDetail("Address", formatAddress(address))
              ]}
            />
          )}
        </div>
        <Space />
        <div>
          <FormGroup center>
            <H5 withDetails>Account Details</H5>
          </FormGroup>
          {isActive("editEmailAddress") ? (
            <BlackoutOverlay onClick={() => this.setState({ activeSection: -1 })}>
              <EditEmailAddress
                onUpdate={this.handleEmailAddressUpdate}
                updateEmailAddressEndpoint={accountEmailAddressEndpoint}
                description={changeEmailDescription}
                changeEmailEndpoint={changeEmailEndpoint}
                prefillFields={pick(["email"], customer)} />
            </BlackoutOverlay>
          ) : (
          <div>
            <Summary disableBase items={[{
                ...createDetail("Email", email),
                editText: "Edit",
                onClick: () => {
                  this.setState({
                    activeSection: editableSections.indexOf("editEmailAddress")
                  }, () => {
                    gtmHandler({
                      pageCategory: "MyMazda - Manage Account",
                      pageAction: `PERSONAL DETAILS - "EMAIL" - clicked "EDIT"`
                    })
                  })
                }
            }]} />
              {emailPendingApproval && <div style={{ display: "flex", alignItems: "center" }}>
                <AlertIcon style={{ marginTop: "-0.15em", height: "1em", width: "1em", paddingRight: "0.5em" }} />
                <p style={{ fontSize: "90%", color: COLOR_PALETTE.GRAY_DARK, fontWeight: 400 }}>
                  Verification email sent. Pending approval.
                  <span style={[
                    verificationEmailResent ? { opacity: 0.5, pointerEvents: "none" } : {},
                    { transition: "opacity 0.25s", marginLeft: "0.5em" }
                  ]}>
                    <Hyperlink styleName="black" onClick={this.handleResendEmail}>
                      {verificationEmailResent ? "Sent" : "Resend"}
                    </Hyperlink>
                  </span>
                </p>
              </div>}
            </div>
          )}
          {isActive("editPassword") ? (
            <BlackoutOverlay>
              <EditPassword
                gtmEvent={"GTM"}
                changePasswordEndpoint={changePasswordEndpoint}
                onUpdate={this.handlePasswordUpdate}
                email={email}
              />
            </BlackoutOverlay>
          ) : (
            <Summary
              disableBase
              items={[
                {
                  ...createDetail("Password", Array(11).join("*")),
                  editText: "Edit",
                  onClick: () => {
                    this.setState({
                      activeSection: editableSections.indexOf("editPassword")
                    }, () => {
                      gtmHandler({
                        pageCategory: "MyMazda - Manage Account",
                        pageAction: `PERSONAL DETAILS - "PASSWORD" - clicked "EDIT"`
                      })
                    })
                  }
                }
              ]}
            />
          )}
        </div>
      </div>
    )
  }
}

MyMazdaAccountDetails.propTypes = {
  heading: PropTypes.string,
  changePasswordEndpoint: PropTypes.string,
  accountSettingsEndpoint: PropTypes.string,
  changeEmailDescription: PropTypes.string,
  emailPendingApproval: PropTypes.bool,
  resendEmailEndpoint: PropTypes.string,
  customer: PropTypes.shape({
    firstName: PropTypes.string,
    surname: PropTypes.string,
    mobilePhone: PropTypes.string,
    address: PropTypes.object,
    email: PropTypes.string
  })
}

const mapDispatchToProps = dispatch => ({
  flashMessage: (...args) => dispatch(actionCreators.addFlashMessage(...args)),
  refreshCurrentRoute: () => dispatch(actionCreators.refreshCurrentRoute())
})

export default withRadium(connect(null, mapDispatchToProps))(MyMazdaAccountDetails)



// WEBPACK FOOTER //
// ./src/containers/mymazda/manage-account/account-details.jsx